import * as Sentry from '@sentry/browser'

/**
 * Version-drift guard (client side).
 *
 * After a deploy, a browser can keep running a page of the previous release
 * (open tab, back/forward cache, disk cache). The next Livewire roundtrip then
 * morphs new-release markup into old-release Alpine scopes and dies with
 * ReferenceErrors ("inSelectionMode is not defined", Sentry 2026-07-06).
 *
 * The server stamps every web response with X-App-Version and every page with
 * a same-named meta tag (PreventStaleFrontend middleware). This guard reloads
 * the page as soon as the two diverge — BEFORE any morph runs:
 *
 *  1. Livewire response from a newer deploy → cancel the request (verified:
 *     sendRequest checks isCancelled() before any message is processed) and
 *     reload.
 *  2. wire:navigate swapped in a newer page while this JS runtime is still the
 *     old bundle → reload.
 *  3. Page revived from the back/forward cache (the classic back-button case)
 *     → verify against /version.json and reload proactively, before the user
 *     interacts with a zombie page.
 */

const RELOAD_GUARD_KEY = 'membergy:version-drift-reloaded-at'
const RELOAD_LOOP_WINDOW_MS = 30000
const DOCUMENT_ID_KEY = '__membergyVersionDriftDocumentId'
const MEMORY_STORAGE_KEY = '__membergyVersionDriftMemoryStorage'
const noop = () => {}

const currentMetaVersion = () => document.querySelector('meta[name="app-version"]')?.content || null

const currentDocumentId = () => {
  if (!window[DOCUMENT_ID_KEY]) {
    window[DOCUMENT_ID_KEY] = window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
  }

  return window[DOCUMENT_ID_KEY]
}

const currentMemoryStorage = () => {
  if (!window[MEMORY_STORAGE_KEY]) {
    const values = new Map()

    window[MEMORY_STORAGE_KEY] = {
      getItem: (key) => values.get(key) ?? null,
      setItem: (key, value) => values.set(key, value),
      removeItem: (key) => values.delete(key)
    }
  }

  return window[MEMORY_STORAGE_KEY]
}

const resilientStorage = (providedStorage) => {
  const fallback = currentMemoryStorage()
  let primary = providedStorage

  if (primary === undefined) {
    try {
      primary = window.sessionStorage
    } catch {
      primary = null
    }
  }

  return {
    getItem: (key) => {
      try {
        return primary?.getItem(key) ?? fallback.getItem(key)
      } catch {
        return fallback.getItem(key)
      }
    },
    setItem: (key, value) => {
      // Mirror every write so a later SecurityError can fall back without
      // losing the same-document lock shared by duplicate bundles.
      fallback.setItem(key, value)

      try {
        primary?.setItem(key, value)
      } catch {
        // Storage can be unavailable in restricted WebViews/privacy modes.
      }
    },
    removeItem: (key) => {
      fallback.removeItem(key)

      try {
        primary?.removeItem(key)
      } catch {
        // The in-memory fallback is sufficient for this document.
      }
    }
  }
}

const readReloadMarker = (storage, now) => {
  const rawMarker = storage.getItem(RELOAD_GUARD_KEY)

  if (!rawMarker) {
    return null
  }

  let marker

  try {
    marker = JSON.parse(rawMarker)
  } catch {
    storage.removeItem(RELOAD_GUARD_KEY)

    return null
  }

  const age = now() - marker?.requestedAt
  const valid = marker &&
    typeof marker.requestedAt === 'number' &&
    typeof marker.documentId === 'string' &&
    typeof marker.fromVersion === 'string' &&
    typeof marker.toVersion === 'string' &&
    Number.isFinite(age) &&
    // A clock correction into the past must fail safe and keep the loop cap.
    age < RELOAD_LOOP_WINDOW_MS

  if (valid) {
    return marker
  }

  // Timestamp-only markers from the previous implementation parse as JSON,
  // but cannot distinguish a duplicate bundle from a completed reload.
  storage.removeItem(RELOAD_GUARD_KEY)

  return null
}

export default function startVersionDriftGuard(Livewire, {
  storage: providedStorage,
  browserLocation = window.location,
  now = Date.now,
  documentId: providedDocumentId,
  readCurrentVersion = currentMetaVersion,
  captureMessage = Sentry.captureMessage
} = {}) {
  const bootVersion = readCurrentVersion()

  if (!bootVersion) {
    return noop
  }

  const storage = resilientStorage(providedStorage)
  const documentId = providedDocumentId ?? currentDocumentId()

  // Once a needed reload had to be suppressed (loop guard), this runtime is
  // known-drifted: from then on every wire:navigate is escalated to a full
  // browser navigation so no further SPA swaps can mix releases — the next
  // click heals the page with fresh HTML + assets from a single response.
  let hardNavigationRequired = false
  let reloadRequested = false
  let persistentDriftReported = false

  const reloadForNewVersion = (source, observedVersion, { force = false } = {}) => {
    // location.reload() does not stop the current JavaScript task. In a
    // cross-build wire:navigate both Livewire bundles can therefore forward
    // the same navigated event before unload. Only the first callback belongs
    // to the reload attempt; later callbacks in this document are duplicates.
    if (reloadRequested) {
      return true
    }

    // A second copy of this module can be booted by the new Vite bundle while
    // wire:navigate is still finishing in the old document. Its closure has
    // its own reloadRequested flag, so use the document-bound marker as the
    // shared lock across both bundle instances.
    const recentReload = readReloadMarker(storage, now)

    if (recentReload?.documentId === documentId) {
      reloadRequested = true

      return true
    }

    // A different document proves that the browser completed a reload, but
    // not that the deploy is consistent. Keep the same 30-second cap for
    // A→B→A rolling-deploy flips as for a page that stayed on version A.
    if (recentReload && !persistentDriftReported) {
      captureMessage(`version drift persists after reload (${source})`, {
        level: 'warning',
        extra: {
          previousReload: {
            requestedAt: recentReload.requestedAt,
            documentId: recentReload.documentId,
            fromVersion: recentReload.fromVersion,
            toVersion: recentReload.toVersion,
            source: recentReload.source ?? null,
            loadedVersion: bootVersion,
            reachedTargetVersion: recentReload.toVersion === bootVersion
          },
          currentDrift: {
            documentId,
            fromVersion: bootVersion,
            toVersion: observedVersion,
            source
          }
        }
      })
      persistentDriftReported = true
    }

    if (recentReload && !force) {
      hardNavigationRequired = true

      return false
    }

    reloadRequested = true
    storage.setItem(RELOAD_GUARD_KEY, JSON.stringify({
      requestedAt: now(),
      documentId,
      fromVersion: bootVersion,
      toVersion: observedVersion,
      source
    }))
    browserLocation.reload()

    return true
  }

  const handleNavigate = (event) => {
    if (!hardNavigationRequired) {
      return
    }

    event.preventDefault()
    browserLocation.assign(event.detail.url)
  }

  document.addEventListener('livewire:navigate', handleNavigate)

  // 1) Livewire response rendered by a newer deploy.
  const removeRequestInterceptor = Livewire.interceptRequest(({ request, onParsed }) => {
    // onParsed (not onResponse): the body is fully consumed here, so
    // request.cancel() can no longer abort a running body read.
    onParsed(({ response }) => {
      const serverVersion = response?.headers?.get?.('X-App-Version')

      if (serverVersion && serverVersion !== bootVersion) {
        // Cancel unconditionally — a cross-release response must never be
        // morphed into this page's scopes, even while the reload-loop guard
        // is holding back the actual reload (e.g. mixed versions during a
        // rolling deploy). Worst case is a ~30s window of dropped updates,
        // after which the guard allows the next reload attempt.
        request.cancel()
        reloadForNewVersion('livewire-response', serverVersion)
      }
    })
  })

  // 2) wire:navigate pulled in a page of a newer deploy. The swap already
  //    happened, so the page MUST NOT keep running old JS against the new
  //    markup — reload even inside the loop window (force). That cannot spin:
  //    every occurrence requires a user-triggered navigation, and the forced
  //    full load always yields a consistent document on which meta version
  //    and boot version match again.
  const handleNavigated = () => {
    const pageVersion = readCurrentVersion()

    if (pageVersion && pageVersion !== bootVersion) {
      reloadForNewVersion('wire-navigate', pageVersion, { force: true })
    }
  }

  document.addEventListener('livewire:navigated', handleNavigated)

  // 3) bfcache restore (back button): check the server before the page is used.
  const handlePageShow = (event) => {
    if (!event.persisted) {
      return
    }

    fetch('/version.json', { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const serverVersion = data?.full_version ?? data?.version

        if (serverVersion && serverVersion !== bootVersion) {
          reloadForNewVersion('bfcache-restore', serverVersion)
        }
      })
      .catch(() => {}) // best effort — offline/flaky network must not break the page
  }

  window.addEventListener('pageshow', handlePageShow)

  let cleanedUp = false

  return () => {
    if (cleanedUp) {
      return
    }

    cleanedUp = true
    document.removeEventListener('livewire:navigate', handleNavigate)
    document.removeEventListener('livewire:navigated', handleNavigated)
    window.removeEventListener('pageshow', handlePageShow)

    if (typeof removeRequestInterceptor === 'function') {
      removeRequestInterceptor()
    }
  }
}
